import { lazy, type ComponentType } from 'react'

/**
 * A lazy route that survives a deploy.
 *
 * Every page here is a separate chunk, named by content hash. A deploy writes new hashes and
 * removes the old ones — so anybody with the app already open is holding an `index.html` that
 * names files which no longer exist. The next page they visit fetches a 404, `lazy()` throws,
 * and they get an error screen on a page that is perfectly fine. It looks like that page is
 * broken; it is only out of date.
 *
 * CloudFront makes the window wider than it sounds: `index.html` is served
 * `s-maxage=31536000`, so the edge can hand out a year-old document naming long-deleted
 * chunks until something invalidates it.
 *
 * The fix is the one the browser can perform itself — fetch the document again and get the
 * current chunk names with it. Reloading is safe here in a way it would not be mid-form,
 * because this fires before the page has rendered anything to lose.
 *
 * Guarded by `sessionStorage` so a genuinely missing chunk cannot become a reload loop: one
 * attempt per chunk per tab, and after that the error surfaces properly and RouteError
 * explains it.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  key: string,
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory()
      // Cleared on success so a later deploy gets its own attempt.
      safeRemove(`reloaded:${key}`)
      return mod
    } catch (error) {
      if (!isMissingChunk(error) || safeGet(`reloaded:${key}`)) throw error
      safeSet(`reloaded:${key}`, '1')
      window.location.reload()
      // Never resolves — the reload replaces this document. Returning anything would flash a
      // half-rendered page in the moment before it goes.
      return new Promise<{ default: T }>(() => {})
    }
  })
}

/**
 * A chunk that is gone, as opposed to a component that threw while loading.
 *
 * Browsers word this differently — Chrome and Safari say "Failed to fetch dynamically imported
 * module", Firefox says "error loading dynamically imported module" — and a reload fixes none
 * of the other reasons an import can fail, so the match stays narrow deliberately.
 */
function isMissingChunk(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(message)
}

// sessionStorage throws outright in some privacy modes, and a storage failure must not become
// the reason a page will not load.
const safeGet = (k: string) => {
  try {
    return sessionStorage.getItem(k)
  } catch {
    return null
  }
}
const safeSet = (k: string, v: string) => {
  try {
    sessionStorage.setItem(k, v)
  } catch {
    /* nothing to do — the guard degrades to "no reload", which is the safe direction */
  }
}
const safeRemove = (k: string) => {
  try {
    sessionStorage.removeItem(k)
  } catch {
    /* as above */
  }
}
